import { AppProvider } from './context/AppContext';
import { ThemeProvider } from './context/ThemeProvider';
import { ToastProvider } from './context/ToastContext';
import MainLayout from './components/MainLayout/MainLayout';
import TopBar from './components/TopBar/TopBar';
import PanelContainer from './components/PanelLayout/PanelContainer';
import EditorPanel from './components/Editor/EditorPanel';
import PreviewPanel from './components/Preview/PreviewPanel';
import ExportModal from './components/ExportModal/ExportModal';
import FullScreenContainer from './components/FullScreen/FullScreenContainer';
import { ToastContainer } from './components/Toast/ToastContainer';
import { useAutoSave } from './hooks/useAutoSave';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useAccessibility } from './hooks/useAccessibility';

// Component that uses the auto-save hook (must be inside providers)
const AppContent = () => {
  // Enable auto-save with 2-second delay
  useAutoSave({
    delay: 2000,
    enabled: true,
  });

  // Enable keyboard shortcuts
  useKeyboardShortcuts();

  // Enable accessibility features
  const { announce, getAccessibilityClasses } = useAccessibility({
    announceChanges: true,
  });

  const accessibilityClasses = getAccessibilityClasses();

  return (
    <div className={`app-container ${
      accessibilityClasses.reducedMotion ? 'reduce-motion' : ''
    } ${
      accessibilityClasses.highContrast ? 'high-contrast' : ''
    }`}>
      {/* Skip to main content link for screen readers and keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only cs-focus-ring"
        onFocus={() => announce('Skip to main content link focused', 'polite')}
      >
        Skip to main content
      </a>

      <FullScreenContainer>
        <MainLayout>
          <TopBar />
          <main
            id="main-content"
            role="main"
            aria-label="Markdown editor and preview"
            className="flex-1 flex flex-col min-h-0"
          >
            <PanelContainer>
              <EditorPanel />
              <PreviewPanel />
            </PanelContainer>
          </main>
        </MainLayout>
      </FullScreenContainer>

      {/* Modals and overlays */}
      <ExportModal />
      <ToastContainer />

      {/* Hidden landmarks for screen readers */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        Content Studio - Professional Markdown Editor
      </div>
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <ThemeProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </ThemeProvider>
    </AppProvider>
  );
}

export default App;